import { useSkuSearch } from '@/hooks/useSkuSearch'
import type { SkuSearchResult } from '@/types/sku'
import { NewSkuBadge } from './NewSkuBadge'
import {
  SearchSuggestCell,
  type SearchSuggestResults,
} from './SearchSuggestCell'

export interface SkuSaveData {
  skuNumber: string
  description: string | null
  isNewSku: boolean
  // Additional fields from SKU search for auto-fill
  vendorId: string | null
  vendorName: string | null
  department: string | null
  class_: string | null
  targetRetail: string | null
  targetCost: string | null
}

interface SkuSearchCellProps {
  value: string | null
  isNewSku?: boolean
  onSave: (data: SkuSaveData) => void
  isEditing: boolean
  onCancelEdit: () => void
  allowNewSku?: boolean
  onTab?: (shiftKey: boolean) => boolean | void
  onEnter?: (shiftKey: boolean) => void
}

const EMPTY_SKU_FIELDS = {
  description: null,
  vendorId: null,
  vendorName: null,
  department: null,
  class_: null,
  targetRetail: null,
  targetCost: null,
}

function useSkuResults(
  query: string,
  enabled: boolean
): SearchSuggestResults<SkuSearchResult> {
  const { data, isLoading } = useSkuSearch({ query, enabled })
  return { results: data?.data ?? [], isLoading }
}

export function SkuSearchCell({
  value,
  isNewSku,
  onSave,
  isEditing,
  onCancelEdit,
  allowNewSku = true,
  onTab,
  onEnter,
}: SkuSearchCellProps) {
  return (
    <SearchSuggestCell<SkuSearchResult>
      value={value}
      isEditing={isEditing}
      onCancelEdit={onCancelEdit}
      onTab={onTab}
      onEnter={onEnter}
      placeholder="Search SKU..."
      useResults={useSkuResults}
      getKey={result => result.sku_id ?? result.sku_number}
      getCopyText={result => result.sku_number}
      renderItem={result => (
        <>
          <span className="font-medium text-[#556374]">
            {result.sku_number}
          </span>
          {result.description && (
            <span className="text-xs text-[#556374] truncate max-w-[180px]">
              {result.description}
            </span>
          )}
        </>
      )}
      onSelect={result =>
        onSave({
          skuNumber: result.sku_number,
          description: result.description,
          isNewSku: false,
          vendorId: result.vendor_id,
          vendorName: result.vendor_name,
          department: result.department_code,
          class_: result.category_code,
          targetRetail: result.last_retail,
          targetCost: result.last_cost,
        })
      }
      onClear={() =>
        onSave({ skuNumber: '', isNewSku: false, ...EMPTY_SKU_FIELDS })
      }
      extraOption={(query, results) => ({
        // Offer "New SKU" when the typed value isn't an exact match
        show:
          allowNewSku &&
          query.length >= 1 &&
          !results.some(
            result => result.sku_number.toLowerCase() === query.toLowerCase()
          ),
        copyText: query,
        onSelect: () =>
          onSave({ skuNumber: query, isNewSku: true, ...EMPTY_SKU_FIELDS }),
        render: highlighted => (
          <div
            className={`cursor-pointer px-2 py-1.5 text-sm flex items-center justify-between gap-2 ${
              highlighted ? 'bg-[#FEF2F3]' : ''
            }`}
          >
            <span className="font-medium text-[#CF1F2E]">{query}</span>
            <span className="text-xs italic text-[#CF1F2E]">New SKU</span>
          </div>
        ),
      })}
      display={
        <div className="min-h-[2rem] flex items-center gap-2 px-1">
          <span>{value || '-'}</span>
          {allowNewSku && isNewSku && <NewSkuBadge />}
        </div>
      }
    />
  )
}
